import React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Chip } from '../ui/chip';
import { getStatusTailwindClasses } from '../../utils/statusUtils';
import { statusLabel, subStatusLabel } from '../../utils/webcheckinStatus';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/skeleton';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * Check-in Table Component
 * Styled like TripsDataTable for consistency
 * 
 * @param {Object} props - Component props
 * @param {Array} props.checkIns - Array of check-in data
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @param {Function} props.onRowClick - Callback when row is clicked
 * @param {Object} props.pagination - Pagination state
 * @param {Function} props.onPageChange - Page change callback
 * @param {Function} props.onRowsPerPageChange - Rows per page change callback
 * @param {string} props.sortBy - Current sort column
 * @param {string} props.sortDirection - Current sort direction
 * @param {Function} props.onSort - Sort callback
 */
const CheckInTable = ({
  checkIns,
  loading,
  error,
  onRowClick,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortDirection,
  onSort
}) => {
  // Format date and time separately without timezone conversion
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: 'N/A' };
    try {
      const [datePart, timePart] = dateString.split('T');
      const timeOnly = timePart ? timePart.replace('Z', '').split('.')[0] : '00:00:00';
      const [hours, minutes] = timeOnly.split(':');

      const [year, month, day] = datePart.split('-');
      const formattedDate = `${day}/${month}/${year}`;

      const hour24 = parseInt(hours);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      const formattedTime = `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;

      return { date: formattedDate, time: formattedTime };
    } catch (error) {
      console.error('Error formatting date/time:', error);
      return { date: 'Invalid date', time: 'Invalid time' };
    }
  };

  // Format scheduled time - show empty string if falsy
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formatScheduledTime = React.useCallback((dateString) => {
    if (!dateString) return { date: '', time: '' };
    return formatDateTime(dateString);
  }, []);

  // Define columns
  const columns = React.useMemo(() => [
    {
      accessorKey: 'id',
      header: ({ column }) => {
        const isSorted = sortBy === 'id';
        const direction = isSorted ? sortDirection : null;
        return (
          <div 
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => onSort?.('id')}
          >
            ID
            {!direction && <ArrowUpDown className="h-3 w-3 text-gray-400" />}
            {direction === 'asc' && <ArrowUp className="h-3 w-3 text-blue-600" />}
            {direction === 'desc' && <ArrowDown className="h-3 w-3 text-blue-600" />}
          </div>
        );
      },
      cell: ({ row }) => (
        <span className="font-medium">{row.original.id}</span>
      ),
    },
    {
      accessorKey: 'emails',
      header: 'Emails',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1 max-w-[160px]">
          {row.original.emails?.map((email, index) => (
            <Chip
              key={index}
              label={email}
              size="small"
              color="primary"
              className="text-xs"
            />
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'flightNumber',
      header: ({ column }) => {
        const isSorted = sortBy === 'flightNumber';
        const direction = isSorted ? sortDirection : null;
        return (
          <div 
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => onSort?.('flightNumber')}
          >
            Airline
            {!direction && <ArrowUpDown className="h-3 w-3 text-gray-400" />}
            {direction === 'asc' && <ArrowUp className="h-3 w-3 text-blue-600" />}
            {direction === 'desc' && <ArrowDown className="h-3 w-3 text-blue-600" />}
          </div>
        );
      },
      cell: ({ row }) => {
        const airlineCode = row.original.flightNumber 
          ? row.original.flightNumber.substring(0, 2) 
          : '';
        return <span className="text-gray-700">{airlineCode}</span>;
      },
    },
    {
      accessorKey: 'sector',
      header: ({ column }) => {
        const isSorted = sortBy === 'origin';
        const direction = isSorted ? sortDirection : null;
        return (
          <div 
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => onSort?.('origin')}
          >
            Sector
            {!direction && <ArrowUpDown className="h-3 w-3 text-gray-400" />}
            {direction === 'asc' && <ArrowUp className="h-3 w-3 text-blue-600" />}
            {direction === 'desc' && <ArrowDown className="h-3 w-3 text-blue-600" />}
          </div>
        );
      },
      cell: ({ row }) => {
        const sector = `${row.original.origin || ''}-${row.original.destination || ''}`;
        return <span className="font-medium text-gray-700">{sector}</span>;
      },
    },
    {
      accessorKey: 'departureDate',
      header: ({ column }) => {
        const isSorted = sortBy === 'departureDate';
        const direction = isSorted ? sortDirection : null;
        return (
          <div 
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => onSort?.('departureDate')}
          >
            Departure
            {!direction && <ArrowUpDown className="h-3 w-3 text-gray-400" />}
            {direction === 'asc' && <ArrowUp className="h-3 w-3 text-blue-600" />}
            {direction === 'desc' && <ArrowDown className="h-3 w-3 text-blue-600" />}
          </div>
        );
      },
      cell: ({ row }) => {
        const departureDateTime = formatDateTime(row.original.departureDate);
        return (
          <div>
            <div className="font-medium text-gray-900">{departureDateTime.date}</div>
            <div className="text-gray-500 text-xs">{departureDateTime.time}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'scheduledTime',
      header: ({ column }) => {
        const isSorted = sortBy === 'scheduledTime';
        const direction = isSorted ? sortDirection : null;
        return (
          <div 
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => onSort?.('scheduledTime')}
          >
            Scheduled (UTC)
            {!direction && <ArrowUpDown className="h-3 w-3 text-gray-400" />}
            {direction === 'asc' && <ArrowUp className="h-3 w-3 text-blue-600" />}
            {direction === 'desc' && <ArrowDown className="h-3 w-3 text-blue-600" />}
          </div>
        );
      },
      cell: ({ row }) => {
        const scheduledDateTime = formatScheduledTime(row.original.scheduledTime);
        if (!scheduledDateTime.date) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div>
            <div className="font-medium text-gray-900">{scheduledDateTime.date}</div>
            <div className="text-gray-500 text-xs">{scheduledDateTime.time}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => {
        const isSorted = sortBy === 'status';
        const direction = isSorted ? sortDirection : null;
        return (
          <div 
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => onSort?.('status')}
          >
            Status
            {!direction && <ArrowUpDown className="h-3 w-3 text-gray-400" />}
            {direction === 'asc' && <ArrowUp className="h-3 w-3 text-blue-600" />}
            {direction === 'desc' && <ArrowDown className="h-3 w-3 text-blue-600" />}
          </div>
        );
      },
      cell: ({ row }) => {
        const checkIn = row.original;
        return (
          <div className="space-y-1">
            <Chip
              label={statusLabel(checkIn.status)}
              size="small"
              className={`text-xs ${getStatusTailwindClasses(checkIn.status)}`}
            />
            {checkIn.status === 'completed' && checkIn.flightPassengerStats?.length > 0 && (
              <div className="text-xs text-blue-600 font-medium">
                BP: {checkIn.flightPassengerStats.map(stat =>
                  `${stat.passengersWithBoardingPass}/${stat.totalPassengers}`
                ).join(', ')}
              </div>
            )}
            {checkIn.sub_status && checkIn.sub_status !== 'NONE' && (
              <div className="text-xs text-blue-600 font-medium">
                {subStatusLabel(checkIn.sub_status)}
              </div>
            )}
            {checkIn.status !== 'completed' && checkIn.statusMessage && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-xs text-gray-500 truncate max-w-[100px] cursor-help">
                    {checkIn.statusMessage}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">{checkIn.statusMessage}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ], [sortBy, sortDirection, onSort, formatScheduledTime]);

  const table = useReactTable({
    data: checkIns || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      onRowClick
    },
  });

  // Loading state
  if (loading) {
    return (
      <div className="w-full max-w-full h-full flex flex-col">
        <div className="overflow-auto rounded-md border flex-1 min-h-0">
          <div className="p-8 space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex gap-2">
                <Skeleton className="h-8 w-12" />
                <Skeleton className="h-8 flex-1" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full max-w-full h-full flex flex-col">
        <div className="overflow-auto rounded-md border flex-1 min-h-0 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!checkIns || checkIns.length === 0) {
    return (
      <div className="w-full max-w-full h-full flex flex-col">
        <div className="overflow-auto rounded-md border flex-1 min-h-0 p-8 text-center">
          <p className="text-gray-500">No check-ins found matching your filters.</p>
        </div>
      </div>
    );
  }

  const { pageNumber, itemsPerPage, totalItems } = pagination || {};
  const totalPages = Math.ceil((totalItems || checkIns.length) / itemsPerPage);

  // Handle page changes
  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  return (
    <TooltipProvider>
      <div className="w-full h-full flex flex-col overflow-hidden">
        <div className="overflow-auto flex-1 border rounded-md">
          <Table className="w-full min-w-[600px] border-collapse">
            <TableHeader className="sticky top-0 z-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="bg-gray-50 hover:bg-gray-50 border-b">
                  {headerGroup.headers.map((header) => (
                    <TableHead 
                      key={header.id} 
                      className="h-10 px-3 text-xs whitespace-nowrap bg-gray-50 font-semibold text-gray-700"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="bg-white">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="cursor-pointer hover:bg-blue-50 border-b border-gray-100"
                    onClick={() => {
                      if (onRowClick) {
                        onRowClick(row.original);
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="px-3 py-2 text-sm">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No check-ins found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="flex items-center justify-between space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              Showing {(pageNumber - 1) * itemsPerPage + 1} to {Math.min(pageNumber * itemsPerPage, totalItems)} of{" "}
              {totalItems} entries
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-sm text-muted-foreground">
                Page {pageNumber} of {totalPages || 1}
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pageNumber - 1)}
                  disabled={pageNumber <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pageNumber + 1)}
                  disabled={pageNumber >= (totalPages || 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default CheckInTable;
